import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	PRODUCT_FEATURE_SURFACES,
	type ProductUsageAllCustomersPeriod,
	type ProductUsageAllCustomersRow,
} from "@signalist/shared-types";

import { useUsageAllCustomers } from "../../../redux/feature-usage/feature-usage.selectors";
import { formatCount } from "../utils/featureUsage";
import { MetricTitle } from "./MetricHelp";
import { PeriodFilter } from "./UsageFilters";
import { UsageTimeline } from "./UsageTimeline";
import { FeatureCell, UsageColumn, UsageStatus, UsageSummary, UsageTable } from "./UsageTable";

export function AllCustomersTab() {
	const { t } = useTranslation();
	const [period, setPeriod] = useState<ProductUsageAllCustomersPeriod>(365);
	const query = useUsageAllCustomers(period);
	const { data } = query;
	const columns: UsageColumn<ProductUsageAllCustomersRow>[] = [
		{
			key: "feature",
			title: <MetricTitle metric="feature" />,
			render: (row) => <FeatureCell feature={row} />,
		},
		{
			key: "allEvents",
			title: <MetricTitle metric="allEvents" />,
			render: (row) => formatCount(row.events),
		},
		{
			key: "dailyEvents",
			title: <MetricTitle definition="allDaily" metric="dailyEvents" />,
			render: (row) => (
				<UsageTimeline
					days={data?.days ?? []}
					label={`${row.label}: ${t("featureUsage.metrics.dailyEvents")}`}
					values={row.dailyEvents}
				/>
			),
		},
		{
			key: "surfaceEvents",
			title: <MetricTitle metric="surfaceEvents" />,
			render: (row) => (
				<div className="grid grid-cols-2 gap-2 text-xs">
					{PRODUCT_FEATURE_SURFACES.map((surface) => (
						<span key={surface}>
							{t(`featureUsage.surfaces.${surface}`)} {formatCount(row.surfaceEvents[surface])}
						</span>
					))}
				</div>
			),
		},
	];

	return (
		<>
			<div className="flex flex-wrap items-center justify-between gap-4">
				<h2 className="text-sm font-semibold text-text">{t("featureUsage.allHeading")}</h2>
				<PeriodFilter all value={period} onChange={setPeriod} />
			</div>
			<p className="my-4 rounded bg-brand-light/5 p-3 text-xs text-text-meta">
				{t("featureUsage.allNote")}
			</p>
			<UsageStatus {...query} retry={query.refetch} />
			{data && !query.isError && !query.isLoading && data.lastRolledUpDate !== null && (
				<>
					<UsageSummary data={data} />
					<UsageTable areas={data.areas} columns={columns} label={t("featureUsage.tabs.all")} />
				</>
			)}
		</>
	);
}
